import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import * as internalToolsApi from 'api/internaltools';
import {
  ENTITY_OWNER_DOCUMENT_TYPES,
  KYB_COMPANY_ADDRESS_LOOKUPS,
  KYB_COMPANY_DETAILS_LOOKUPS,
  KYB_NOT_AVAILABLE,
  KYB_STATUS,
} from 'modules/merchantinfo/constants/constants';
import {
  buildKybDetailSections,
  buildKybLegacyRenderedKeys,
  formatKybScalarValue,
} from 'modules/merchantinfo/utils/kybDetails';
import { showError, showToast } from 'utils/notifications';
import {
  calculateNumofAdditionalBusinessDocs,
  calculateNumOfCompanyOwners,
  formatDateTime,
  formatString,
  snakeToTitleCase,
} from 'utils/utils';
import LoadingErrorWrapper from 'components/LoadingErrorWrapper/LoadingErrorWrapper';
import ShowKeyAndValue from 'components/ShowKeyAndValue/ShowKeyAndValue';

const NOT_AVAILABLE = KYB_NOT_AVAILABLE;
const COMPANY_OWNERS_PARENT_BASE = 'company_owners';

const formatOptionValue = (raw) => formatKybScalarValue(raw);

const KYB_STATUS_BADGE_COLORS = {
  [KYB_STATUS.SUCCEEDED]: 'green',
  [KYB_STATUS.APPROVED]: 'green',
  [KYB_STATUS.INITIATED]: 'yellow',
  [KYB_STATUS.NOT_INITIATED]: 'gray',
};

const formatPhoneNumber = (raw) => {
  try {
    const parsed = JSON.parse(raw);

    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const formatted = [parsed.dial_code, parsed.phone].filter(Boolean).join(' ');

      return formatted || raw;
    }

    return raw;
  } catch {
    return raw;
  }
};

const formatList = (raw) => {
  try {
    const parsed = JSON.parse(raw);

    if (Array.isArray(parsed)) {
      return parsed.length ? parsed.join(', ') : NOT_AVAILABLE;
    }

    return raw;
  } catch {
    return raw;
  }
};

const formatBoolean = (raw) => {
  if (raw === 'true' || raw === true) return 'Yes';
  if (raw === 'false' || raw === false) return 'No';

  return raw;
};

export default function ShowKybDetails({ entity }) {
  const [kyb, setKyb] = useState(null);
  const [kybDetailsList, setKybDetailsList] = useState([]);
  const [kybSummary, setKybSummary] = useState(null);
  const [numOfCompanyOwners, setNumOfCompanyOwners] = useState(0);
  const [additionalBusinessDocsCount, setAdditionalBusinessDocsCount] = useState(0);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef(null);

  const handleSubmitToPartner = async () => {
    setIsSubmitting(true);
    try {
      const response = await internalToolsApi.submitKybToPartner({ kyb_id: entity?.kyb_id });
      const result = response?.data?.data;

      setSubmitResult(result);
      showToast(
        toast,
        'Submitted to partner',
        `KYB submitted for review${result?.partner_status ? ` (status: ${result.partner_status})` : ''}.`,
        'success'
      );
      onClose();
    } catch (submitError) {
      showError(
        toast,
        'Failed to submit KYB to partner',
        submitError,
        'Something went wrong while submitting the KYB to the partner.'
      );
    }
    setIsSubmitting(false);
  };

  const getEntityKybDetails = async () => {
    setIsLoading(true);
    try {
      const response = await internalToolsApi.getExhaustiveKybDetailsById(
        entity?.kyb_id,
        process.env.REACT_APP_KYB_VIEW_PASSWORD
      );
      const kybData = response?.data?.data;
      const kybDetails = kybData?.details;

      const kybMap = {};

      kybDetails?.forEach((detail) => {
        // Skip placeholder/empty rows that the source occasionally emits.
        if (!detail?.key || detail.key === 'null') {
          return;
        }

        kybMap[`${detail.key}_${detail.parent}`] = detail;
      });

      const numOfCompanyOwners = calculateNumOfCompanyOwners(kybMap);
      const numOfAdditionalBusinessDocs = calculateNumofAdditionalBusinessDocs(kybMap);

      setKyb(kybMap);
      setKybDetailsList(kybDetails ?? []);
      setKybSummary({
        status: kybData?.status,
        lastUpdatedAt: kybData?.last_updated_at,
        initiatedAt: kybData?.initiated_at,
        isAcknowledged: kybData?.is_acknowledged,
      });
      setNumOfCompanyOwners(numOfCompanyOwners);
      setAdditionalBusinessDocsCount(numOfAdditionalBusinessDocs);
    } catch (error) {
      setError(error);
    }
    setIsLoading(false);
  };

  const additionalSections = useMemo(() => {
    const renderedKeys = buildKybLegacyRenderedKeys(kyb, { numOfCompanyOwners, additionalBusinessDocsCount });

    return buildKybDetailSections(kybDetailsList, renderedKeys);
  }, [kyb, kybDetailsList, numOfCompanyOwners, additionalBusinessDocsCount]);

  const companyOwnerSections = additionalSections.filter(
    (section) => section?.parentBase === COMPANY_OWNERS_PARENT_BASE
  );
  const isRenderedOwnerSection = (section) =>
    section?.parentBase === COMPANY_OWNERS_PARENT_BASE &&
    section?.index !== null &&
    section?.index < numOfCompanyOwners;
  const otherSections = additionalSections.filter((section) => !isRenderedOwnerSection(section));

  const renderSectionRows = (rows) =>
    rows?.map((row) => (
      <ShowKeyAndValue
        key={row?.id}
        keyName={row?.keyName}
        value={row?.value}
        linkText={row?.linkText}
        helperText={row?.helperText}
      />
    ));

  const getValue = (keys, formatter) => {
    const keyList = Array.isArray(keys) ? keys : [keys];

    for (const key of keyList) {
      const raw = kyb?.[key]?.value;

      if (raw !== undefined && raw !== null && raw !== '') {
        return formatter ? formatter(raw) : raw;
      }
    }

    return NOT_AVAILABLE;
  };

  const renderOwnerAdditionalRows = (ownerIndex) => {
    const ownerSection = companyOwnerSections.find((section) => section?.index === ownerIndex);

    if (!ownerSection?.rows?.length) return null;

    return (
      <>
        <Heading as='h1' size='md' mt={4}>
          Other owner details
        </Heading>
        {renderSectionRows(ownerSection.rows)}
      </>
    );
  };

  const renderCompanyOwners = () => {
    const companyOwnersUI = [];

    for (let idx = 0; idx < numOfCompanyOwners; ++idx) {
      const ownerKey = (key) => `${key}_company_owners[${idx}]`;

      companyOwnersUI.push(
        <Flex key={`companyOwner - ${idx}`} direction='column' gap={3}>
          <Heading as='h1' size='md' mt={3} color='gray'>
            Company owner {idx + 1}
          </Heading>
          <ShowKeyAndValue keyName='First name' value={getValue(ownerKey('first_name'))} />
          <ShowKeyAndValue keyName='Last name' value={getValue(ownerKey('last_name'))} />
          <ShowKeyAndValue keyName='Phone number' value={getValue(ownerKey('phone_number'), formatPhoneNumber)} />
          <ShowKeyAndValue keyName='Email address' value={getValue(ownerKey('email'))} />
          <ShowKeyAndValue keyName='Date of Birth' value={getValue(ownerKey('date_of_birth'))} />
          <ShowKeyAndValue keyName='Primary nationality' value={getValue(ownerKey('primary_nationality'))} />
          <ShowKeyAndValue keyName='Relationship to company' value={getValue(ownerKey('owner_profile'))} />
          <ShowKeyAndValue keyName='Other relationship to company' value={getValue(ownerKey('owner_profile_other'))} />
          <ShowKeyAndValue keyName='Owner title' value={getValue(ownerKey('owner_title'))} />
          <ShowKeyAndValue keyName='Ownership percentage' value={getValue(ownerKey('ownership_percentage'))} />
          <ShowKeyAndValue keyName='Is director' value={getValue(ownerKey('is_director'), formatBoolean)} />
          <ShowKeyAndValue keyName='Is signer' value={getValue(ownerKey('is_signer'), formatBoolean)} />
          <ShowKeyAndValue keyName='Has control' value={getValue(ownerKey('has_control'), formatBoolean)} />
          <ShowKeyAndValue keyName='Has ownership' value={getValue(ownerKey('has_ownership'), formatBoolean)} />

          <ShowKeyAndValue keyName='Passport issuing country' value={getValue(ownerKey('passport_issuing_country'))} />
          <ShowKeyAndValue keyName='Passport ID number' value={getValue(ownerKey('passport_id_number'))} />

          <Heading as='h1' size='md' mt={4}>
            Tax information
          </Heading>
          <ShowKeyAndValue keyName='Tax Identification Number' value={getValue(ownerKey('tax_info_number'))} />
          <ShowKeyAndValue keyName='Tax Identification Country' value={getValue(ownerKey('tax_info_country'))} />

          <Heading as='h1' size='md' mt={4}>
            Home Address
          </Heading>
          <ShowKeyAndValue keyName='Country' value={getValue(ownerKey('country'))} />
          <ShowKeyAndValue keyName='Street address' value={getValue(ownerKey('address1'))} />
          <ShowKeyAndValue keyName='City' value={getValue(ownerKey('city'))} />
          <ShowKeyAndValue keyName='State' value={getValue(ownerKey('state'))} />
          <ShowKeyAndValue keyName='ZIP or postal code' value={getValue(ownerKey('zip_code'))} />

          <Heading as='h1' size='md' mt={4}>
            Owner&apos;s documents
          </Heading>

          {ENTITY_OWNER_DOCUMENT_TYPES.map((documentType) => {
            if (Object.prototype.hasOwnProperty.call(kyb, ownerKey(documentType?.value))) {
              return (
                <ShowKeyAndValue
                  key={documentType?.value}
                  keyName={documentType?.label}
                  value={getValue(ownerKey(documentType?.value))}
                />
              );
            }

            return null;
          })}

          {renderOwnerAdditionalRows(idx)}
        </Flex>
      );
    }

    if (companyOwnersUI.length === 0) {
      companyOwnersUI.push(
        <Text key='noCompanyOwners' color='gray'>
          No company owners information available.
        </Text>
      );
    }

    return companyOwnersUI;
  };

  const renderAdditionalBusinessDocuments = () => {
    const additionalBusinessDocumentsUI = [];

    for (let idx = 0; idx < additionalBusinessDocsCount; ++idx) {
      additionalBusinessDocumentsUI.push(
        <ShowKeyAndValue
          key={idx}
          keyName={
            formatString(kyb[`document[${idx}]_additional_business_document`]?.message, '_') ??
            `Additional Document ${idx + 1}`
          }
          value={getValue(`document[${idx}]_additional_business_document`)}
        />
      );
    }

    ENTITY_OWNER_DOCUMENT_TYPES.forEach((documentType) => {
      if (Object.prototype.hasOwnProperty.call(kyb, `${documentType?.value}_business_document`)) {
        additionalBusinessDocumentsUI.push(
          <ShowKeyAndValue
            key={documentType?.value}
            keyName={documentType?.label}
            value={getValue(`${documentType?.value}_business_document`)}
          />
        );
      }
    });

    return additionalBusinessDocumentsUI;
  };

  useEffect(() => {
    getEntityKybDetails();
  }, []);

  return (
    <Box>
      <LoadingErrorWrapper isLoading={isLoading} error={error} errorTitle='Error fetching KYB details.'>
        {kyb && (
          <VStack align='left' gap={4}>
            {kybSummary?.status && (
              <HStack spacing={3} mt={4} align='center'>
                <Badge colorScheme={KYB_STATUS_BADGE_COLORS[kybSummary.status] ?? 'gray'} rounded='full' px={3} py={1}>
                  {snakeToTitleCase(kybSummary.status)}
                </Badge>
                {kybSummary?.initiatedAt && (
                  <Text fontSize='sm' color='gray.500'>
                    Initiated {formatDateTime(kybSummary.initiatedAt)}
                  </Text>
                )}
                {kybSummary?.lastUpdatedAt && (
                  <Text fontSize='sm' color='gray.500'>
                    Last updated {formatDateTime(kybSummary.lastUpdatedAt)}
                  </Text>
                )}
                <Text fontSize='sm' color='gray.500'>
                  {kybSummary?.isAcknowledged ? 'Acknowledged' : 'Not acknowledged'}
                </Text>
              </HStack>
            )}

            <Box>
              <Heading as='h1' size='lg' mt={4} mb={3}>
                Company details
              </Heading>
              <Flex direction='column' gap={3}>
                <ShowKeyAndValue
                  keyName='Legal Business Name'
                  value={getValue(KYB_COMPANY_DETAILS_LOOKUPS.legalBusinessName)}
                />
                <ShowKeyAndValue
                  keyName='Company name (Doing business as)'
                  value={getValue(KYB_COMPANY_DETAILS_LOOKUPS.companyName)}
                />
                <ShowKeyAndValue
                  keyName='Date of Incorporation'
                  value={getValue(KYB_COMPANY_DETAILS_LOOKUPS.dateOfIncorporation)}
                />
                <ShowKeyAndValue
                  keyName='Company type'
                  value={getValue(KYB_COMPANY_DETAILS_LOOKUPS.companyType, formatOptionValue)}
                />
                <ShowKeyAndValue
                  keyName='Ownership structure'
                  value={getValue(KYB_COMPANY_DETAILS_LOOKUPS.ownershipStructure, formatOptionValue)}
                />
                <ShowKeyAndValue
                  keyName='Source of Funds'
                  value={getValue(KYB_COMPANY_DETAILS_LOOKUPS.sourceOfFunds, formatList)}
                />
                <ShowKeyAndValue
                  keyName='Purpose of account'
                  value={getValue(KYB_COMPANY_DETAILS_LOOKUPS.purpose, formatOptionValue)}
                />
                <ShowKeyAndValue
                  keyName='Company website (Optional)'
                  value={getValue(KYB_COMPANY_DETAILS_LOOKUPS.website)}
                />
                <ShowKeyAndValue
                  keyName='Company Tax Identification Number'
                  value={getValue(KYB_COMPANY_DETAILS_LOOKUPS.taxIdentificationNumber)}
                />
                <ShowKeyAndValue
                  keyName='Company phone number'
                  value={getValue(KYB_COMPANY_DETAILS_LOOKUPS.phoneNumber, formatPhoneNumber)}
                />
                <ShowKeyAndValue keyName='Company email address' value={getValue(KYB_COMPANY_DETAILS_LOOKUPS.email)} />
                <ShowKeyAndValue
                  keyName='Company industry type'
                  value={getValue(KYB_COMPANY_DETAILS_LOOKUPS.industryType, formatOptionValue)}
                />
                <ShowKeyAndValue
                  keyName='Description of business'
                  value={getValue(KYB_COMPANY_DETAILS_LOOKUPS.businessDescription)}
                />
                <ShowKeyAndValue
                  keyName='Countries of Operation'
                  value={getValue(KYB_COMPANY_DETAILS_LOOKUPS.countryOfOperations, formatList)}
                />
                <ShowKeyAndValue
                  keyName='Total Number of Employees'
                  value={getValue(KYB_COMPANY_DETAILS_LOOKUPS.totalEmployees)}
                />
                <ShowKeyAndValue
                  keyName='Annual Turn Over'
                  value={getValue(KYB_COMPANY_DETAILS_LOOKUPS.annualTurnover)}
                />
                <ShowKeyAndValue
                  keyName='Estimated Annual Revenue (USD)'
                  value={getValue(KYB_COMPANY_DETAILS_LOOKUPS.estimatedAnnualRevenue, formatOptionValue)}
                />
                <ShowKeyAndValue
                  keyName='Country of Incorporation'
                  value={getValue(KYB_COMPANY_DETAILS_LOOKUPS.countryOfIncorporation)}
                />
                <ShowKeyAndValue
                  keyName='State of Incorporation'
                  value={getValue(KYB_COMPANY_DETAILS_LOOKUPS.stateOfIncorporation)}
                />
                <ShowKeyAndValue
                  keyName='Monthly Virtual Asset Deposit'
                  value={getValue(KYB_COMPANY_DETAILS_LOOKUPS.monthlyVirtualAssetDeposit)}
                />
                <ShowKeyAndValue
                  keyName='Monthly Virtual Asset Withdrawal'
                  value={getValue(KYB_COMPANY_DETAILS_LOOKUPS.monthlyVirtualAssetWithdrawal)}
                />
                <ShowKeyAndValue
                  keyName='Monthly Fiat Deposit'
                  value={getValue(KYB_COMPANY_DETAILS_LOOKUPS.monthlyFiatDeposit, formatOptionValue)}
                />
                <ShowKeyAndValue
                  keyName='Monthly Fiat Withdrawal'
                  value={getValue(KYB_COMPANY_DETAILS_LOOKUPS.monthlyFiatWithdrawal)}
                />
              </Flex>
            </Box>

            <Divider variant='dashed' />

            <Box>
              <Heading as='h1' size='lg' mb={3}>
                Company address
              </Heading>
              <Heading as='h2' size='md' mb={3}>
                Registered address
              </Heading>
              <Flex direction='column' gap={3} mb={3}>
                <ShowKeyAndValue keyName='Country' value={getValue(KYB_COMPANY_ADDRESS_LOOKUPS.registeredCountry)} />
                <ShowKeyAndValue
                  keyName='Street address'
                  value={getValue(KYB_COMPANY_ADDRESS_LOOKUPS.registeredAddress1)}
                />
                <ShowKeyAndValue keyName='City' value={getValue(KYB_COMPANY_ADDRESS_LOOKUPS.registeredCity)} />
                <ShowKeyAndValue keyName='State' value={getValue(KYB_COMPANY_ADDRESS_LOOKUPS.registeredState)} />
                <ShowKeyAndValue
                  keyName='ZIP or postal code'
                  value={getValue(KYB_COMPANY_ADDRESS_LOOKUPS.registeredZipCode)}
                />
              </Flex>
              <Heading as='h2' size='md' mb={3}>
                Current address
              </Heading>
              <Flex direction='column' gap={3}>
                <ShowKeyAndValue
                  keyName='Same as registered address'
                  value={getValue(KYB_COMPANY_ADDRESS_LOOKUPS.currentSameAsRegistered, formatBoolean)}
                />
                <ShowKeyAndValue keyName='Country' value={getValue(KYB_COMPANY_ADDRESS_LOOKUPS.currentCountry)} />
                <ShowKeyAndValue
                  keyName='Street address'
                  value={getValue(KYB_COMPANY_ADDRESS_LOOKUPS.currentAddress1)}
                />
                <ShowKeyAndValue keyName='City' value={getValue(KYB_COMPANY_ADDRESS_LOOKUPS.currentCity)} />
                <ShowKeyAndValue keyName='State' value={getValue(KYB_COMPANY_ADDRESS_LOOKUPS.currentState)} />
                <ShowKeyAndValue
                  keyName='ZIP or postal code'
                  value={getValue(KYB_COMPANY_ADDRESS_LOOKUPS.currentZipCode)}
                />
              </Flex>
            </Box>

            <Divider variant='dashed' />

            <Box>
              <Heading as='h1' size='lg' mb={3}>
                Company owner
              </Heading>
              <Flex direction='column' gap={3}>
                {renderCompanyOwners()}
              </Flex>
            </Box>

            <Divider variant='dashed' />

            <Box>
              <Heading as='h1' size='lg' mb={3}>
                Company document
              </Heading>
              <Flex direction='column' gap={3}>
                <ShowKeyAndValue
                  keyName={kyb['document_type_business_document']?.value ?? 'Document'}
                  value={getValue('document_business_document')}
                />

                {renderAdditionalBusinessDocuments()}
              </Flex>
            </Box>

            {otherSections.map((section) => (
              <Box key={section?.parent}>
                <Divider variant='dashed' mb={4} />
                <Heading as='h1' size='lg' mb={3}>
                  {section?.label}
                </Heading>
                <Flex direction='column' gap={3}>
                  {renderSectionRows(section?.rows)}
                </Flex>
              </Box>
            ))}

            <Divider variant='dashed' />

            <Box>
              <Flex direction='column' gap={2} align='flex-start'>
                {submitResult && (
                  <Text color='green.600' fontWeight='medium'>
                    Submitted to partner
                    {submitResult?.partner_status ? ` — status: ${submitResult.partner_status}` : ''}
                    {submitResult?.submitted_at ? ` (at ${submitResult.submitted_at})` : ''}
                  </Text>
                )}
                <Button
                  colorScheme='blue'
                  onClick={onOpen}
                  isLoading={isSubmitting}
                  isDisabled={!entity?.kyb_id || Boolean(submitResult)}
                >
                  Submit to Partner
                </Button>
                <Text fontSize='sm' color='gray.500'>
                  Submits this KYB to the onboarding partner (1Money) for compliance review.
                </Text>
              </Flex>
            </Box>
          </VStack>
        )}
      </LoadingErrorWrapper>

      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize='lg' fontWeight='bold'>
              Submit KYB to Partner
            </AlertDialogHeader>
            <AlertDialogBody>
              This will submit the business account to 1Money for compliance review. This action cannot be undone. Make
              sure all company details, owners and documents are correct before proceeding.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose} isDisabled={isSubmitting}>
                Cancel
              </Button>
              <Button colorScheme='blue' onClick={handleSubmitToPartner} isLoading={isSubmitting} ml={3}>
                Confirm &amp; Submit
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
